import {
  isTestable,
  meterReadingRequired,
} from '@ecosconnect/core/src/helpers/backflow/helpers';
import {
  AssemblyType,
  AssemblyTypeKey,
  JobFormDataInput,
  RpCheck2Type,
  TestRepair,
  TestResult,
  TestResultBase,
} from '@ecosconnect/core/src/types/jobFormData';
import Accordion from '../Accordion/Accordion';
import AccordionItem from '../Accordion/AccordionItem';
import React, {
  EventHandler,
  SyntheticEvent,
  useCallback,
  useMemo,
} from 'react';
import { AssemblyInfo } from './types';
import BackflowTestResultEdit from './result/BackflowTestResultEdit';
import { Input } from '../Form';
import ButtonToggle from '../Button/ButtonToggle';
import { shutoffOptions } from './options';
import Button from '../Button/Button';
import { getTestStatus } from '@ecosconnect/core/src/helpers/backflow/status';
import { Flex } from 'rebass';
import { SetFormValue } from '../Job/JobThirdPane';

type BackflowTestReportEditProps = {
  formData: JobFormDataInput;
  formAssembly?: AssemblyInfo;
  assemblyType?: AssemblyTypeKey;
  rpCheck2Type: RpCheck2Type;
  onFormInputChange: EventHandler<any>;
  setFormValue: SetFormValue;
};

const BackflowTestReportEdit: React.FC<BackflowTestReportEditProps> = ({
  formData,
  assemblyType,
  rpCheck2Type,
  onFormInputChange,
  setFormValue,
}) => {
  const testable = isTestable(formData) && !!assemblyType;
  const tests = formData.tests;
  const testsWithPlaceholder = useMemo<typeof tests>(() => {
    return tests && tests.length === 0 ? [{}] : tests;
  }, [tests]);
  const hasTests = !!testsWithPlaceholder;
  const hasFailure =
    testable &&
    hasTests &&
    testsWithPlaceholder[0] &&
    getTestStatus(
      testsWithPlaceholder[0],
      AssemblyType[assemblyType],
      rpCheck2Type
    ) === 'FAIL';

  const canAddRetest =
    hasFailure &&
    testsWithPlaceholder?.length === 2 &&
    testsWithPlaceholder[1].type === 'repair';

  const canAddRepair = hasFailure && testsWithPlaceholder?.length === 1;

  const onTestChange = useCallback(
    (test: TestResult | TestRepair, index: number) => {
      setFormValue('tests', (oldTests) => {
        const tests = oldTests?.slice() ?? [];
        tests[index] = test;
        return tests as JobFormDataInput['tests'];
      });
    },
    [formData, setFormValue]
  );

  const addTest = useCallback(
    (type: 'TEST' | 'REPAIR' | SyntheticEvent = 'TEST') => {
      setFormValue('tests', (oldTests) => {
        oldTests = oldTests ?? [];
        const newTest: TestResultBase | TestRepair = {
          type: type === 'REPAIR' ? 'repair' : undefined,
        };
        return [...oldTests, newTest] as any;
      });
    },
    [setFormValue]
  );

  const removeTest = useCallback(
    (index: number) => {
      setFormValue('tests', (oldTests) => {
        const tests = oldTests?.slice() ?? [];
        tests.splice(index, 1);
        return tests as any;
      });
    },
    [setFormValue]
  );

  return (
    <>
      {testable &&
        hasTests &&
        testsWithPlaceholder.map((test: TestResult | TestRepair, index) => {
          return (
            <BackflowTestResultEdit
              key={index}
              index={index}
              test={test}
              onChange={onTestChange}
              assemblyType={assemblyType as AssemblyTypeKey}
              rpCheck2Type={rpCheck2Type}
              removeTest={removeTest}
            />
          );
        })}
      {(canAddRepair || canAddRetest) && (
        <Flex px={20} py={5}>
          {canAddRepair && (
            <Button mr={2} onClick={() => addTest('REPAIR')} icon="wrench">
              Add Repair
            </Button>
          )}
          {canAddRetest && (
            <Button onClick={addTest} icon="repeat">
              Add Retest
            </Button>
          )}
        </Flex>
      )}
      <Accordion title="Additional">
        <AccordionItem
          label="Meter Reading"
          alignItems="center"
          as="label"
          value={
            <Input
              onChange={onFormInputChange}
              variant="accordion"
              required={
                assemblyType && meterReadingRequired(AssemblyType[assemblyType])
              }
              type="number"
              name="meterReading"
              value={formData.meterReading}
              step="1"
              min="0"
              max="1000"
              autoComplete="off"
            />
          }
        />
        <AccordionItem
          label="Shutoff 1"
          alignItems="center"
          value={
            <ButtonToggle
              name="shutoff1Position"
              onClick={onFormInputChange}
              buttonOptions={shutoffOptions}
              my={-1}
              // height={27}
              // fontSize="11px"
              // lineHeight={1}
              value={
                typeof formData.shutoff1Position !== 'number'
                  ? null
                  : formData.shutoff1Position
              }
            />
          }
        />
        <AccordionItem
          label="Shutoff 2"
          alignItems="center"
          value={
            <ButtonToggle
              name="shutoff2Position"
              onClick={onFormInputChange}
              buttonOptions={shutoffOptions}
              my={-1}
              value={
                typeof formData.shutoff2Position !== 'number'
                  ? null
                  : formData.shutoff2Position
              }
            />
          }
        />
      </Accordion>
    </>
  );
};

export default BackflowTestReportEdit;
